import { Card, CardContent, CardHeader, CardTitle, CardDescription, LoadingState } from "@nthamizh/ui";
import { useCurrentUser } from "@platform/hooks";
import { ShieldCheck, User, Mail, Calendar, Building2, KeyRound } from "lucide-react";

function InfoRow({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-3 py-2">
      <Icon className="w-4 h-4 text-gray-400 shrink-0" />
      <span className="text-sm text-gray-500 w-28 shrink-0">{label}</span>
      <span className="text-sm text-gray-900 font-medium">{value}</span>
    </div>
  );
}

function currentTenantLabel(): string {
  const host = window.location.hostname;
  const parts = host.split(".");
  if (parts.length <= 2) return "Platform (no enterprise)";
  return `Enterprise: ${parts[0]}`;
}

const AUTH_PROVIDER_LABEL: Record<string, string> = {
  password: "Email + password",
  google: "Google Sign-In",
};

const PLATFORM_ROLE_BADGE: Record<string, string> = {
  platform_admin: "bg-purple-100 text-purple-700",
  support: "bg-blue-100 text-blue-700",
};

export function AccessPage() {
  const { data: user, isLoading } = useCurrentUser();

  if (isLoading || !user) {
    return <LoadingState label="Loading your access..." />;
  }

  const platformRoleLabel = user.platformRole
    ? user.platformRole === "platform_admin"
      ? "Platform Admin"
      : "Support"
    : null;

  const enterpriseRoles = user.roles ?? [];
  const permissions = user.permissions ?? {};

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">My Access</h1>
        <p className="text-sm text-gray-500 mt-1">
          Your identity and resolved permissions, as seen by the API right now.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-4 h-4" />
            Identity
          </CardTitle>
        </CardHeader>
        <CardContent className="divide-y divide-gray-100">
          <InfoRow icon={User} label="Name" value={user.name} />
          <InfoRow icon={Mail} label="Email" value={user.email} />
          <InfoRow icon={Building2} label="Tenant" value={currentTenantLabel()} />
          <InfoRow
            icon={KeyRound}
            label="Sign-in method"
            value={AUTH_PROVIDER_LABEL[user.authProvider] ?? user.authProvider}
          />
          <InfoRow
            icon={Calendar}
            label="Member since"
            value={new Date(user.createdAt).toLocaleDateString()}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4" />
            Roles and permissions
          </CardTitle>
          <CardDescription>
            Enforced server-side on every protected endpoint — this page only reflects them.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {platformRoleLabel && (
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                Platform role
              </p>
              <span
                className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                  PLATFORM_ROLE_BADGE[user.platformRole!]
                }`}
              >
                {platformRoleLabel}
              </span>
            </div>
          )}

          {enterpriseRoles.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                Enterprise roles
              </p>
              <div className="flex flex-wrap gap-2">
                {enterpriseRoles.map((r) => (
                  <span
                    key={r.roleId}
                    className="inline-block px-2.5 py-1 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700"
                  >
                    {r.roleName}
                  </span>
                ))}
              </div>
            </div>
          )}

          {Object.keys(permissions).length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                Module permissions
              </p>
              <div className="space-y-3">
                {Object.entries(permissions).map(([moduleKey, perms]) => (
                  <div key={moduleKey}>
                    <p className="text-xs font-medium text-gray-700 capitalize mb-1">{moduleKey}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {Object.entries(perms)
                        .filter(([, v]) => v)
                        .map(([perm]) => (
                          <span
                            key={perm}
                            className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded"
                          >
                            {perm.replace(/_/g, " ")}
                          </span>
                        ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!platformRoleLabel && enterpriseRoles.length === 0 && (
            <p className="text-sm text-gray-500">No roles assigned yet.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
